
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UserProfileData {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profileImageUrl: string | null;
  userId: string | null;
}

export const useUserProfile = () => {
  const [userData, setUserData] = useState<UserProfileData>({
    firstName: null,
    lastName: null,
    email: null,
    profileImageUrl: null,
    userId: null
  });
  
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    
    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Error fetching user:", userError);
        setLoading(false);
        return null;
      }
      
      console.log("User authenticated:", user.id);
      
      // Get profile image URL from user metadata
      const metadata = user.user_metadata || {};
      const profileImageUrl = metadata.profile_image_url || null;
      
      // Set user data
      const profileData = {
        firstName: metadata.first_name || null,
        lastName: metadata.last_name || null,
        email: user.email,
        profileImageUrl: profileImageUrl,
        userId: user.id
      };
      
      setUserData(profileData);
      return user.id;
    } catch (error) {
      console.error("Unexpected error in fetchUserProfile:", error);
      toast.error("Error loading account data");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  
  const updateProfileImage = async (file: File): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You need to be logged in to update your profile");
        return null;
      }
      
      // Create a unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      // Upload the file
      const { error: uploadError, data } = await supabase.storage
        .from('profiles')
        .upload(fileName, file, { upsert: true });
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('profiles')
        .getPublicUrl(fileName);
      
      // Update user metadata with the profile image URL
      const { error: updateError } = await supabase.auth.updateUser({
        data: { profile_image_url: urlData.publicUrl }
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Update local state
      setUserData(prev => ({
        ...prev,
        profileImageUrl: urlData.publicUrl
      }));
      
      return urlData.publicUrl;
    } catch (error) {
      console.error("Error updating profile image:", error);
      toast.error("Failed to update profile image");
      return null;
    }
  };

  return {
    userData,
    loading,
    fetchUserProfile,
    updateProfileImage
  };
};
